import { activityFamily, restSlotPlain } from "./weekScheduleGrid";
import { dtPageLine, resolveDtStep } from "../constants/dtPlaybook";

export function shortProblem(text = "", n = 160) {
  const t = String(text || "")
    .replace(/\\n/g, "\n")
    .replace(/^▶\s*/gm, "")
    .replace(/^(Problem Statement|Problem|Focus)\s*:\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  const sentence = t.split(/(?<=[.!?])\s+/)[0] || t;
  return sentence.length > n ? `${sentence.slice(0, n).trim()}…` : sentence;
}

function labeled(content, key) {
  const m = String(content || "")
    .replace(/\\n/g, "\n")
    .match(new RegExp(`(?:^|\\n)\\s*(?:▶\\s*)?${key}\\s*:\\s*([^\\n]+)`, "i"));
  return m ? m[1].trim() : "";
}

export function extractProblemFromRows(rows = [], fallback = "") {
  const list = Array.isArray(rows) ? rows : [];
  for (const r of list) {
    const act = Array.isArray(r) ? r[2] : r?.activity;
    const content = Array.isArray(r) ? r[3] : r?.content;
    if (/problem review|problem lab|problem statement/i.test(String(act || ""))) {
      const hit =
        labeled(content, "Problem Statement") ||
        labeled(content, "Problem") ||
        labeled(content, "Focus") ||
        String(content || "").replace(/\\n/g, "\n").split("\n").map((l) => l.replace(/^▶\s*/, "").trim()).find((l) => l.length > 20);
      if (hit) return shortProblem(hit);
    }
    const ps = labeled(content, "Problem Statement");
    if (ps) return shortProblem(ps);
  }
  return shortProblem(fallback);
}

export function replaceDoneWhen(text = "") {
  return String(text || "")
    .replace(/\\n/g, "\n")
    .replace(/(^|\n)\s*(?:▶\s*)?Done when\s*:/gi, "$1▶ Deliverable:");
}

function isWorkbenchText(t = "") {
  return /sns agent workbench|n8n|what agent to build|workflow steps|workbench/i.test(t);
}

function isOkrionText(t = "") {
  return /\bokrion\b|\bokr\b|linkedin|certif|badge|career goal|weekly objective/i.test(t);
}

const OKRION_BY_DOW = [
  { title: "LinkedIn article (OKRion)", task: "Draft 8–12 lines in OKRion about what you learned and how it helps your project. Do not post yet." },
  { title: "Certification slice (OKRion)", task: "Open OKRion → today’s certification / course objective. Finish one module or 20 min of practice." },
  { title: "LinkedIn comments (OKRion)", task: "In OKRion, log 2 thoughtful comments on posts about today’s topic (not “great post”)." },
  { title: "Skill badge (OKRion)", task: "In OKRion, tick one skill/badge step (certification quiz, lab, or course checkpoint)." },
  { title: "Publish + weekly OKR (OKRion)", task: "Publish or finalize the LinkedIn draft in OKRion and mark this week’s career OKR done/partial." },
];

export function okrionForDay(dow = 0, ctx = {}) {
  const pick = OKRION_BY_DOW[Math.abs(Number(dow) || 0) % OKRION_BY_DOW.length];
  const problem = ctx.problem || "your project problem";
  const topic = ctx.topic || "today's topic";
  return { ...pick, problem, topic };
}

function ensureLine(text, re, line) {
  if (re.test(text)) return text;
  return `${line}\n${text}`.trim();
}

function ensureDeliverable(text, deliverable) {
  if (/(?:^|\n)\s*(?:▶\s*)?Deliverable\s*:/i.test(text)) return text;
  return `${text.trim()}\n▶ Deliverable: ${deliverable}`;
}

function buildAgentCell(ctx = {}) {
  const problem = ctx.problem || "the student's problem";
  const topic = ctx.topic || "today's topic";
  const build = ctx.build || "today's tool slice";
  return [
    `▶ Problem (short): ${problem}`,
    `▶ SNS Agent Workbench — work on YOUR project .`,
    `▶ What agent to build (n8n-style): one automation that helps “${build}” for this problem.`,
    `▶ Workflow steps: 1) trigger  2) use today's idea “${topic}”  3) write an output the project can use.`,
    `▶ How it helps their problem: removes one repetitive step in “${problem}”.`,
    `▶ Connect: Learn (${topic}) → System Design → Project (${build}) → this workbench flow.`,
    `▶ Deliverable: named workflow that runs once + screenshot/file the project can open.`,
  ].join("\n");
}

function buildOkrionCell(ctx = {}, existing = "") {
  const dow = Number(ctx.dow || 0);
  const o = okrionForDay(dow, ctx);
  const keep = String(existing || "").replace(/\\n/g, "\n").trim();
  const body = isOkrionText(keep) && !isWorkbenchText(keep) ? keep : "";
  return [
    `▶ Problem (short): ${o.problem}`,
    `▶ OKRion: ${o.title}`,
    `▶ Do this: ${o.task}`,
    `▶ Connect: today's class “${o.topic}” → career proof in OKRion (LinkedIn / cert / badge).`,
    body ? `▶ Notes: ${body.split("\n")[0]}` : null,
    `▶ Deliverable: OKRion log updated (draft URL, cert screenshot, or badge tick).`,
  ].filter(Boolean).join("\n");
}

/** Rewrite one schedule cell so Agent ≠ OKR, problem is visible, Done when → Deliverable. */
export function connectDayCell(activity, content, ctx = {}) {
  const rest = restSlotPlain(activity);
  if (rest) return rest;
  const fam = activityFamily(activity);
  let text = replaceDoneWhen(String(content || "").replace(/\\n/g, "\n"));
  const problem = ctx.problem || "the student's problem";
  const topic = ctx.topic || "today's topic";
  const build = ctx.build || "today's tool slice";

  if (fam === "agent") {
    if (!text.trim() || (isOkrionText(text) && !isWorkbenchText(text))) {
      return buildAgentCell(ctx);
    }
    text = ensureLine(text, /Problem \(short\)|Problem Statement/i, `▶ Problem (short): ${problem}`);
    text = ensureLine(text, /SNS Agent Workbench|What agent to build/i, `▶ SNS Agent Workbench — work on YOUR project.`);
    text = ensureLine(text, /Connect:/i, `▶ Connect: Learn (${topic}) → Design → Project (${build}) → this workbench flow.`);
    return ensureDeliverable(text, `Working workbench flow + screenshot that helps: ${problem}`);
  }

  if (fam === "okr") {
    return buildOkrionCell(ctx, text);
  }

  if (["learning", "sysdesign", "project", "coding", "problem", "standup", "speak", "placement"].includes(fam)) {
    if (fam === "problem" || fam === "standup" || fam === "learning" || fam === "project" || fam === "sysdesign") {
      text = ensureLine(text, /Problem \(short\)|Problem Statement/i, `▶ Problem (short): ${problem}`);
    }
    if (fam === "learning") {
      text = ensureLine(text, /Connect:/i, `▶ Connect: this concept → System Design box → Project (${build}) → Agent Workbench.`);
    }
    if (fam === "sysdesign") {
      text = ensureLine(text, /Connect:/i, `▶ Connect: box for “${topic}” exists because of: ${problem}`);
    }
    if (fam === "project") {
      text = ensureLine(text, /Connect:/i, `▶ Connect: implement the SD box for “${topic}” on the problem: ${problem}`);
    }
    if ((fam === "project" || fam === "problem") && !/DT\s*Playbook\s*page\s*\d/i.test(text)) {
      const step = resolveDtStep(text, null) || ctx.dtStep;
      const line = dtPageLine(step);
      if (line) text = `${line}\n${text}`.trim();
    }
    if (fam === "coding") {
      text = ensureLine(text, /Connect:/i, `▶ Connect: same pattern as class “${topic}” so the tool for ${problem} is easier.`);
    }
    return ensureDeliverable(
      text,
      fam === "project"
        ? `Working slice of “${build}” + 3-line note (what / file / how to demo).`
        : fam === "learning"
          ? `Notes: Concept + Why + where it sits in the tool.`
          : `Proof of today's task (notes, screenshot, or file).`
    );
  }

  return text;
}

export function agentHomeworkBlock(ctx = {}) {
  const problem = ctx.problem || "your project problem";
  const topic = ctx.topic || "today's topic";
  const build = ctx.build || "today's tool slice";
  const dayIdx = (ctx.dayIdx || 0) + 1;
  return [
    `Agent Workbench`,
    `SNS Agent Workbench — continue YOUR project (Day ${dayIdx})`,
    `Problem (short): ${problem}`,
    `Product glance: ${build}`,
    `Today's class hook: ${topic}`,
    `What to build: one n8n-style step that helps “${build}” — not an OKRion / LinkedIn task.`,
    `Step 1: Open the same workbench flow you started in AI Agent Builder.`,
    `Step 2: Add one node that uses “${topic}”.`,
    `Step 3: Run once and save the output the project can open.`,
    `Connect: Learn → Design → Project → this workbench slice.`,
    `Deliverable: named workflow + screenshot/file that helps: ${problem}.`,
  ].join("\n");
}

export function okrionHomeworkBlock(ctx = {}) {
  const o = okrionForDay(ctx.dow, ctx);
  return [
    `LinkedIn`,
    `${o.title} — Week ${ctx.week || 1}`,
    `Problem (short): ${o.problem}`,
    `Your problem (in short): ${o.problem}`,
    `Task: ${o.task} Tie one line to “${o.topic}”.`,
    `Connect: class “${o.topic}” → OKRion career proof (not Agent Builder).`,
    `Deliverable: OKRion entry (draft, cert screenshot, or badge tick) for this night only.`,
  ].join("\n");
}
